// SplashScene - first-view brand beat for the Tactical OLED Arcade redesign.
// Renderer-only: no Core, no data, no network. It shows the live brand/trust signal, then yields to Menu.
import Phaser from 'phaser';
import { PALETTE, FONT, hex } from '../keys';
import { motionAllowed } from '../settings';
import { typePx } from '../ui/typeScale';
import { attachMascot, type Mascot } from '../mascot';
import { styleHeroText, type HeroCut } from '../juice';

export class SplashScene extends Phaser.Scene {
  private routed = false;

  constructor() {
    super('Splash');
  }

  create(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    this.cameras.main.setBackgroundColor(PALETTE.bg);

    this.paintBackdrop(W, H);
    const munchers = this.paintMunchers(W, H);
    this.paintBrand(W, H);
    this.paintTrustLine(W, H);
    this.paintLoader(W, H);
    const hold = new URLSearchParams(window.location.search).get('holdSplash') === '1';
    // #20 SIGNATURE VERB: the muncher lunges + CHOMPs toward the BYT3 rival and a scan ripple sweeps the
    // grid — the splash SHOWS the core verb in its first second instead of a static logo. reduceMotion
    // holds the composed static frame (guard) — no lunge, no ripple.
    if (!hold && motionAllowed()) this.chompLunge(W, H, munchers.player, munchers.rival);

    // The parity capture harness pins the splash open with ?holdSplash=1 so it can be screenshotted
    // deterministically (otherwise it self-routes to Menu after ~1.15s).
    if (!hold) {
      this.input.once('pointerup', () => this.route());
      this.input.keyboard?.once('keydown', () => this.route());
      this.time.delayedCall(1150, () => this.route());
    }
  }

  private route(): void {
    if (this.routed) return;
    this.routed = true;
    this.scene.start('Menu');
  }

  private paintBackdrop(W: number, H: number): void {
    const g = this.add.graphics();
    g.fillStyle(PALETTE.void, 1);
    g.fillRect(0, 0, W, H);
    // faint FULL-BLEED board grid (covers the whole field, like the mockup's ambient grid).
    // FILLED tiles, not hairline strokes: 1px lineStrong strokes at low alpha vanished in captures
    // (scout nit 5: "splash drops bg board-grid"). The mockup's grid is filled cells at ~10% white
    // over its field — which lands on the tile tone — so fill tile-tone cells with a whisper of
    // cell stroke, and paint the bloom AFTER so the ambient light sits over the tiles.
    const cell = Math.min(W * 0.16, H * 0.075);
    const cols = Math.ceil(W / cell) + 1;
    const rows = Math.ceil(H / cell) + 1;
    const left = (W - cols * cell) / 2;
    const top = (H - rows * cell) / 2;
    g.fillStyle(PALETTE.tile, 0.32);
    g.lineStyle(1, PALETTE.lineStrong, 0.11);
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        g.fillRoundedRect(left + c * cell + 3, top + r * cell + 3, cell - 6, cell - 6, 6);
        g.strokeRoundedRect(left + c * cell + 3, top + r * cell + 3, cell - 6, cell - 6, 6);
      }
    }
    // subtle centred cyan bloom (low alpha so it reads as ambient light, not a spotlight disc)
    g.fillStyle(PALETTE.scan, 0.025);
    g.fillCircle(W / 2, H * 0.42, W * 0.6);
  }

  // S3 item 32: the splash reads as TWO tight clusters — (A) mark + wordmark + tagline (identity) and
  // (B) loader + trust line (status) — centred as one column so the old floating voids (mark→wordmark
  // and tagline→loader) collapse into small, intentional group gaps. All Y fractions live here.
  private paintMunchers(W: number, H: number): { player: Mascot; rival: Mascot } {
    // Brand figures = the SAME raster munchers the board uses (mascot unification) — the splash is
    // the first place the player meets the character, so it must be the character they then play.
    // attachMascot handles the idle loop + bob (motion-gated) and the geometric decode fallback.
    const land = W > H;
    const y = land ? H * 0.28 : H * 0.39;
    const playerSize = land ? Math.min(220, Math.max(160, W * 0.15)) : Math.min(230, Math.max(176, W * 0.52));
    const rivalSize = playerSize * (land ? 0.72 : 0.54);
    const player = attachMascot(this, 'player', W / 2 - (land ? W * 0.105 : W * 0.075), y, playerSize);
    const rival = attachMascot(this, 'rival', W / 2 + (land ? W * 0.145 : W * 0.14), y + playerSize * 0.08, rivalSize);
    return { player, rival };
  }

  /** #20 — a 600ms CHOMP-lunge: the player darts toward the rival with a bite squash, the rival recoils,
   *  and a scan ripple pulses out of the impact across the grid. Motion-gated by the caller. */
  private chompLunge(W: number, H: number, player: Mascot, rival: Mascot): void {
    const pObj = player.obj;
    const rObj = rival.obj;
    const px = pObj.x;
    const rx = rObj.x;
    const bx = pObj.scaleX;
    const by = pObj.scaleY;
    this.time.delayedCall(300, () => {
      if (!pObj.active) return;
      // dart toward the rival and back (yoyo), with a bite squash at the closest point
      this.tweens.add({ targets: pObj, x: px + (rx - px) * 0.5, duration: 200, ease: 'Quad.easeIn', yoyo: true, hold: 60 });
      this.tweens.add({ targets: pObj, scaleX: bx * 1.18, scaleY: by * 0.82, duration: 130, delay: 130, yoyo: true, ease: 'Quad.easeOut' });
      // rival flinches away then bobs back
      if (rObj.active) {
        this.tweens.add({ targets: rObj, x: rx + 20, angle: 10, duration: 150, delay: 150, yoyo: true, ease: 'Back.easeOut' });
      }
      this.gridRipple(W, H, (px + rx) / 2, pObj.y);
    });
  }

  /** An expanding scan ring pulsing from (ox, oy) — the "grid ripple" of the splash chomp impact. */
  private gridRipple(W: number, H: number, ox: number, oy: number): void {
    const ring = this.add.graphics().setDepth(1);
    const st = { r: 8, a: 0.55 };
    this.tweens.add({
      targets: st,
      r: Math.max(W, H) * 0.68,
      a: 0,
      duration: 620,
      delay: 130,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        ring.clear();
        ring.lineStyle(2.5, PALETTE.scan, st.a);
        ring.strokeCircle(ox, oy, st.r);
      },
      onComplete: () => ring.destroy(),
    });
  }

  private paintBrand(W: number, H: number): void {
    // #20 + M8 — the wordmark is the BRAND-FACE lockup: bigger chars + tight leading so TRIVIA and
    // MUNCHERS read as ONE stacked mark. M8 gives it the heroWordmark LANGUAGE (two-tone gradient + dark
    // outline + a raised extrude) via juice.styleHeroText, but with BRAND colours (chrome + cyan) instead
    // of the gold hero cut — so the mark keeps its identity and gains poster depth. Sizes off Chakra.
    const land = W > H;
    const size = typePx(this, 'display', { vw: land ? 0.064 : 0.13, min: land ? 58 : 48, max: land ? 86 : 58 });
    const lead = size * 1.06; // near-single leading so the two words read as one stacked mark
    const topY = land ? H * 0.49 : H * 0.51;
    const trk = Math.round(size * 0.03);
    this.paintBrandWord(
      W / 2,
      topY,
      size,
      'TRIVIA',
      { top: '#e7fdff', bottom: '#28e7ff', tracking: trk, stroke: '#02161d', strokeThickness: Math.max(4, size * 0.08) },
      '#075366',
      '#1dd8ff',
    );
    this.paintBrandWord(
      W / 2,
      topY + lead,
      size,
      'MUNCHERS',
      { top: '#ffd1ff', bottom: '#e879f9', tracking: trk, stroke: '#2b0a34' },
      '#5d1f69',
      '#f472ff',
    );
    // tagline — hugs just under the lockup
    const tagText = land ? 'AN ARCADE THAT HAPPENS TO BE TRIVIA' : 'AN ARCADE THAT\nHAPPENS TO BE TRIVIA';
    const tag = this.add
      .text(W / 2, topY + lead + size * (land ? 0.9 : 0.98), tagText, {
        fontFamily: FONT.display,
        fontSize: `${typePx(this, 'caption', { vw: land ? 0.0205 : 0.034, max: land ? 15 : 18 })}px`,
        color: hex(PALETTE.text),
        fontStyle: '600',
        align: 'center',
        lineSpacing: land ? 0 : 10,
      })
      .setOrigin(0.5);
    tag.setLetterSpacing?.(land ? 4 : 5);
    const maxW = W * (land ? 0.62 : 0.82);
    if (tag.width > maxW) tag.setScale(maxW / tag.width);
  }

  /**
   * M8 — one brand word in the heroWordmark LANGUAGE: a raised dark EXTRUDE (stacked offset copies) behind
   * a two-tone GRADIENT + OUTLINE face (juice.styleHeroText with brand-tuned stops). Static style only, so
   * it is reduceMotion- and colourblind-safe (the gradient stays inside the word's own brand hue family).
   */
  private paintBrandWord(cx: number, y: number, size: number, str: string, cut: HeroCut, extrude: string, glow: string): void {
    const steps = Math.max(3, Math.round(size * 0.08));
    for (let i = steps; i >= 1; i -= 1) {
      const e = this.add
        .text(cx, y + i, str, { fontFamily: FONT.display, fontSize: `${size}px`, color: extrude, fontStyle: 'bold', align: 'center' })
        .setOrigin(0.5);
      e.setLetterSpacing?.(cut.tracking ?? -0.5);
    }
    const t = this.add
      .text(cx, y, str, { fontFamily: FONT.display, fontSize: `${size}px`, color: '#ffffff', fontStyle: 'bold', align: 'center' })
      .setOrigin(0.5);
    styleHeroText(t, cut);
    t.setShadow(0, 0, glow, Math.max(10, size * 0.18), true, true);
  }

  private paintTrustLine(W: number, H: number): void {
    const land = W > H;
    const y = H * (land ? 0.77 : 0.73);
    const label = land ? 'AI-written · independently AI-verified' : 'AI-written\nindependently AI-verified';
    const fs = `${typePx(this, 'body', { vw: land ? 0.024 : 0.04, min: 13, max: land ? 18 : 21 })}px`;
    const t = this.add
      .text(0, y, label, { fontFamily: FONT.display, fontSize: fs, color: hex(PALETTE.text), fontStyle: '600', lineSpacing: land ? 0 : 7 })
      .setOrigin(0, 0.5);
    const iconR = land ? 17 : 20;
    const gap = land ? 14 : 18;
    if (!land && t.width > W * 0.58) t.setScale((W * 0.58) / t.width);
    const totalW = iconR * 2.2 + gap + t.displayWidth;
    const startX = W / 2 - totalW / 2;
    // Shield-check mark: outline + check, matching the trust-lockup in the reference.
    const g = this.add.graphics({ x: startX + iconR, y });
    const pts = [
      [0, -0.46],
      [0.4, -0.3],
      [0.4, 0.06],
      [0, 0.48],
      [-0.4, 0.06],
      [-0.4, -0.3],
    ].map(([px, py]) => new Phaser.Geom.Point(px * iconR * 2.4, py * iconR * 2.4));
    g.lineStyle(3, PALETTE.scan, 0.95);
    g.strokePoints(pts, true);
    g.lineStyle(3, PALETTE.text, 0.95);
    g.beginPath();
    g.moveTo(-iconR * 0.42, iconR * 0.02);
    g.lineTo(-iconR * 0.12, iconR * 0.32);
    g.lineTo(iconR * 0.48, -iconR * 0.34);
    g.strokePath();
    t.setX(startX + iconR * 2.2 + gap);
  }

  private paintLoader(W: number, H: number): void {
    // Loader follows the trust lockup like the reference, with enough width to read as a sweep.
    const land = W > H;
    const hold = new URLSearchParams(window.location.search).get('holdSplash') === '1';
    const y = H * (land ? 0.84 : 0.81);
    const w = Math.min(W * (land ? 0.45 : 0.68), land ? 560 : 310);
    const h = 5;
    const label = this.add
      .text(W / 2, y - (land ? 20 : 24), 'LOADING...', {
        fontFamily: FONT.display,
        fontSize: `${typePx(this, 'caption', { vw: land ? 0.017 : 0.028, min: 10, max: land ? 13 : 14 })}px`,
        color: hex(PALETTE.scan),
        fontStyle: '700',
        align: 'center',
      })
      .setOrigin(0.5)
      .setAlpha(0.86);
    label.setLetterSpacing?.(4);
    const g = this.add.graphics();
    g.fillStyle(PALETTE.white, 0.13);
    g.fillRoundedRect(W / 2 - w / 2, y - h / 2, w, h, h / 2);
    g.lineStyle(1, PALETTE.scan, 0.28);
    g.strokeRoundedRect(W / 2 - w / 2, y - h / 2, w, h, h / 2);
    // Determinate cyan load fill. The old short sweep read as an indeterminate shimmer in stills.
    const fill = this.add.graphics();
    const drawFill = (pct: number): void => {
      const fillW = Phaser.Math.Clamp(w * pct, w * 0.18, w);
      fill.clear();
      fill.fillStyle(PALETTE.scan, 1);
      fill.fillRoundedRect(W / 2 - w / 2, y - h / 2, fillW, h, h / 2);
      fill.fillStyle(PALETTE.white, 0.35);
      fill.fillCircle(W / 2 - w / 2 + fillW, y, h * 0.75);
    };
    drawFill(0.68);
    if (!hold && motionAllowed()) {
      const prox = { pct: 0.56 };
      this.tweens.add({
        targets: prox,
        pct: 1,
        duration: 1050,
        ease: 'Cubic.easeOut',
        onUpdate: () => drawFill(prox.pct),
      });
    }
  }
}
